/**
 * Product Field Registry
 * Single source of truth for all Product field definitions — drives grid
 * columns, Manage Columns order, Advanced Search, the product modal, and
 * Excel export via each field's `surfaces` membership (issue #585 Task 5).
 * Supersedes the former split between this file and `flatFields.ts` (now
 * deleted) — those two registries independently drifted out of sync.
 *
 * Field order matches the 12-group taxonomy (`group` on each field) so grid
 * column order, Manage Columns order, and Advanced Search field order all
 * derive from the same order instead of independently hand-maintained lists.
 *
 * NOTE: The following fields are intentionally excluded:
 * - product_id: Auto-generated, handled separately by components
 * - img: Not currently used in UI, may be added later
 */

import {
  Product,
  FlatProduct,
} from '../../services/api/domains/products/types';
import { BaseFieldDefinition } from '../../shared/types/fieldRegistry';
import { createFieldHelpers } from '../../shared/utils/fieldRegistryHelpers';

export const PRODUCT_FIELDS_REGISTRY: BaseFieldDefinition<
  Product & FlatProduct
>[] = [
  // ========== Core Product Information ==========
  {
    key: 'preferred_product_name',
    label: 'Product Name',
    group: 'Core Product Information',
    surfaces: ['grid', 'search', 'modal', 'export'],
    dataType: 'string',
    isSortable: true,
    isFilterable: true,
    isSearchable: true,
    description:
      'Preferred common name for internal reference. Select from synonyms or enter custom name.',
    renderConfig: {
      width: 250,
      maxDisplayLength: 100,
      formatAs: 'html',
    },
  },
  {
    key: 'lgm_product_name',
    label: 'LGM Product Name',
    group: 'Core Product Information',
    surfaces: ['search'],
    dataType: 'string',
    isSortable: false,
    isFilterable: true,
    isSearchable: true,
    description: 'Filter by raw LGM-set product name (preferred_product_name)',
    renderConfig: { width: 250, maxDisplayLength: 100 },
  },
  // man_product_name fully retired (issue #585 Task 5 follow-up) -- its
  // backend column mapping (api/sql/productsFlat.js's PRODUCTS_FLAT_FIELD_MAPPING)
  // was already pn_mfr.name, the exact same Product_Names bucket column the
  // 'manufacturer' field below queries -- a second Advanced Search field with
  // identical results under a near-identical label. 'manufacturer' is the
  // sole surviving field; see its comment below.

  // ========== CAS ==========
  // product_cas_name fully retired (issue #585 Task 4/9) — its TOP-1-by-
  // name_id aggregate arbitrarily picked one of up to 3 simultaneously-
  // active per-source CAS names. Replaced everywhere by the 3 per-source
  // fields below (grid/search) and CASRegistryPanel's per-source name rows
  // (modal). Kept search-only through Task 5, per Task 5.2.1's contract
  // note, until this task's modal-side removal was complete.
  {
    key: 'cas_number',
    label: 'CAS #',
    group: 'CAS',
    surfaces: ['grid', 'search', 'modal', 'export'],
    dataType: 'string',
    isSortable: true,
    isFilterable: true,
    isSearchable: true,
    description: 'Chemical Abstracts Service registry number',
    renderConfig: { width: 150 },
  },
  {
    key: 'is_cas_verified',
    label: 'CAS Verified',
    group: 'CAS',
    surfaces: ['grid', 'search', 'modal', 'export'],
    dataType: 'boolean',
    isReadOnly: true,
    isSortable: true,
    isFilterable: true,
    isSearchable: true,
    description: 'Indicates whether the CAS number has been verified',
    selectionOptions: [
      { value: 'true', label: 'Yes' },
      { value: 'false', label: 'No' },
    ],
    renderConfig: { width: 120 },
  },
  // ========== Product_Names bucket fields (Product Name Architecture,
  // issue #472 T6 continuation, T6.18/AD-25) — one filterable/sortable
  // field per name_role, each spanning all matching Product_Names rows
  // for that role (multi-value, like synonyms). Keys must match
  // PRODUCTS_FLAT_FIELD_MAPPING in api/sql/productsFlat.js exactly. ==========
  {
    key: 'cas_official',
    label: 'CAS Official Name (All Sources)',
    group: 'CAS',
    surfaces: ['search'],
    dataType: 'string',
    isSortable: true,
    isFilterable: true,
    isSearchable: true,
    description:
      'Official CAS name from any confirmed CAS source (Common Chemistry, and future PubChem/GSRS/CompTox sources)',
    renderConfig: { width: 250, maxDisplayLength: 100 },
  },
  // Per-CAS-source variants of cas_official (issue #350 follow-on, promoted
  // to grid columns issue #585 Task 4). Unlike cas_number (identical across
  // sources by construction — AD-10 agree-invariant), a product's
  // cas_official *name* legitimately differs per source (e.g. PubChem's
  // IUPAC name vs. FDA GSRS's preferred name vs. Common Chemistry's registry
  // name). Not surfaced on the modal via this registry — CASRegistryPanel.tsx
  // (Task 9) is a bespoke component with its own data plumbing, not
  // registry-driven.
  {
    key: 'cas_official_common_chemistry',
    label: 'CAS Common Chemistry',
    group: 'CAS',
    surfaces: ['grid', 'search', 'export'],
    dataType: 'string',
    isSortable: true,
    isFilterable: true,
    isSearchable: true,
    description: 'Official CAS name specifically from Common Chemistry',
    // CAS-source official names can contain HTML markup (e.g. <span
    // class="text-smallcaps">, <em>) — strip it for grid display.
    renderConfig: { width: 250, maxDisplayLength: 100, formatAs: 'html' },
  },
  {
    key: 'cas_official_pubchem',
    label: 'CAS PubChem',
    group: 'CAS',
    surfaces: ['grid', 'search', 'export'],
    dataType: 'string',
    isSortable: true,
    isFilterable: true,
    isSearchable: true,
    description: 'Official CAS name specifically from PubChem (IUPAC name)',
    renderConfig: { width: 250, maxDisplayLength: 100, formatAs: 'html' },
  },
  {
    key: 'cas_official_fda_gsrs',
    label: 'CAS FDA GSRS',
    group: 'CAS',
    surfaces: ['grid', 'search', 'export'],
    dataType: 'string',
    isSortable: true,
    isFilterable: true,
    isSearchable: true,
    description:
      'Official CAS name specifically from FDA GSRS (preferred name)',
    renderConfig: { width: 250, maxDisplayLength: 100, formatAs: 'html' },
  },
  {
    key: 'cas_synonym',
    label: 'CAS Synonym',
    group: 'CAS',
    surfaces: ['search'],
    dataType: 'string',
    isSortable: true,
    isFilterable: true,
    isSearchable: true,
    description:
      'CAS-source synonym name (matching only, never a display name)',
    renderConfig: { width: 250, maxDisplayLength: 100 },
  },

  // ========== Manufacturer fields ==========
  {
    key: 'manufacturers',
    label: 'Associated Manufacturers',
    group: 'Manufacturer fields',
    surfaces: ['search', 'modal'],
    dataType: 'array',
    primitiveType: 'object',
    isRelation: true,
    isSortable: true,
    isFilterable: true,
    isSearchable: true,
    relationConfig: {
      type: 'many-to-many',
      entityName: 'manufacturers',
      idField: 'manufacturer_id',
      displayField: 'manufacturer_name',
      searchField: 'manufacturer_name',
      statusField: 'manufacturing_status',
      dataSource: 'api',
      apiEndpoint: '/manufacturers',
    },
    selectionOptions: [
      { value: 'Commercial', label: 'Commercial' },
      { value: 'R&D', label: 'R&D' },
    ],
    renderConfig: {
      width: 300,
      flex: 1,
      maxDisplayItems: 2,
    },
  },
  {
    key: 'manufacturer_name',
    label: 'Manufacturer Company Name',
    group: 'Manufacturer fields',
    // Grid/export column only -- NOT search-surfaced. Task 6 originally also
    // surfaced this as a free-text Advanced Search field, but that collided
    // with the 'manufacturers' relation's own displayField (also
    // 'manufacturer_name'): both configs shared the same key, and the
    // dedup filter in searchConfigAdapter.ts's toFlatAdvancedSearchConfig()
    // kept whichever lacked an apiEndpoint -- i.e. this free-text scalar,
    // silently replacing the proper "select an existing manufacturer"
    // dropdown with a worse duplicate under the same "Manufacturer Company
    // Name" label. Fixed by re-labeling the dropdown (buildIdConfig's
    // 'manufacturer_id' entry) to "Manufacturer Company Name" instead, and
    // dropping 'search' here so there's exactly one entry for this field.
    surfaces: ['grid', 'export'],
    dataType: 'string',
    description: 'Manufacturer name',
    isSortable: true,
    isFilterable: true,
    isSearchable: true,
  },
  {
    key: 'manufacturing_status',
    label: 'Manufacturing Status',
    group: 'Manufacturer fields',
    surfaces: ['grid', 'export'],
    dataType: 'string',
    description: 'Commercial or R&D status',
    isSortable: true,
    isFilterable: true,
  },
  {
    key: 'manufacturer',
    // "(All Sources)" dropped from the label (issue #585 Task 5 follow-up)
    // now that man_product_name is gone -- this is the sole "Manufacturer
    // Product Name" field, nothing left to disambiguate against.
    label: 'Manufacturer Product Name',
    group: 'Manufacturer fields',
    // Promoted to a grid column (issue #585 Task 8) — true multi-value via
    // Task 7's grouping-query pass. Not native-filterable in the grid: MUI's
    // default operators fall back to naive string-matching against an
    // array's comma-joined toString() for an unrecognized dataType, not a
    // real per-item match — Advanced Search's Product_Names bucket-join
    // filtering is unaffected and remains the correct way to filter this.
    surfaces: ['grid', 'search'],
    dataType: 'array',
    primitiveType: 'string',
    isSortable: false,
    isFilterable: false,
    isSearchable: true,
    description:
      'Manufacturer-provided product name across every manufacturer link for this product',
    renderConfig: { width: 250, maxDisplayItems: 3 },
  },

  // ========== Facility fields ==========
  {
    key: 'facilities',
    label: 'Facilities',
    group: 'Facility fields',
    surfaces: ['search', 'modal'],
    dataType: 'array',
    primitiveType: 'object',
    isRelation: true,
    isSortable: true,
    isFilterable: true,
    isSearchable: true,
    relationConfig: {
      type: 'many-to-many',
      entityName: 'facilities',
      idField: 'facility_id',
      displayField: 'facility_name',
      searchField: 'facility_name',
      // facility_status becomes searchable via this field (issue #585
      // Task 6), mirroring manufacturers.relationConfig — toSearchFieldConfig
      // reads statusField + selectionOptions together to build the extra
      // search entry, so selectionOptions lives on this relation, not on
      // the facility_status scalar entry below.
      statusField: 'facility_status',
      dataSource: 'api',
      apiEndpoint: '/facilities',
    },
    selectionOptions: [
      { value: 'APPROVED', label: 'Approved' },
      { value: 'INACTIVE', label: 'Inactive' },
      { value: 'DISQUALIFY', label: 'Disqualify' },
      { value: 'R&D ONLY', label: 'R&D Only' },
      { value: 'ON HOLD', label: 'On Hold' },
      { value: 'NOT MANUF.', label: 'Not Manuf.' },
    ],
    renderConfig: {
      width: 300,
      maxDisplayItems: 2,
    },
  },
  {
    key: 'facility_name',
    // "Facility Name" (not just "Facility") — issue #585 Task 6 promotes this
    // to a search field alongside the pre-existing facility_id ID-based
    // dropdown (label 'Facility'); distinct labels avoid two identically-
    // labeled Advanced Search options.
    label: 'Facility Name',
    group: 'Facility fields',
    // Promoted to a first-class scalar search field (issue #585 Task 6)
    surfaces: ['grid', 'search', 'export'],
    dataType: 'string',
    description: 'Manufacturing facility name',
    isSortable: true,
    isFilterable: true,
    isSearchable: true,
  },
  {
    key: 'country_code',
    label: 'Country',
    group: 'Facility fields',
    // Promoted to a first-class scalar search field (issue #585 Task 6)
    surfaces: ['grid', 'search', 'export'],
    dataType: 'string',
    description: 'ISO country code',
    isSortable: true,
    isFilterable: true,
    isSearchable: true,
  },
  {
    key: 'facility_status',
    label: 'Facility Status',
    group: 'Facility fields',
    // Searchable via facilities.relationConfig.statusField above (issue #585
    // Task 6) — same pattern as manufacturers.relationConfig.statusField.
    // Not itself in the 'search' surface: the searchable entry for this
    // field is emitted from the 'facilities' relation, not from here.
    surfaces: ['grid', 'export'],
    dataType: 'string',
    description: 'Facility operational status',
    isSortable: true,
    isFilterable: true,
  },

  // ========== Grade fields ==========
  {
    key: 'grade',
    label: 'Product Type',
    group: 'Grade fields',
    surfaces: ['search'],
    dataType: 'array',
    primitiveType: 'object',
    isRelation: true,
    isSortable: false,
    isFilterable: true,
    isSearchable: true,
    relationConfig: {
      type: 'many-to-many',
      entityName: 'grades',
      idField: 'grade_id',
      displayField: 'grade_name',
      searchField: 'grade_name',
      dataSource: 'api',
      apiEndpoint: '/grades',
    },
  },
  {
    key: 'grade_name',
    label: 'Product Type',
    group: 'Grade fields',
    surfaces: ['grid', 'export'],
    dataType: 'string',
    description: 'Product grade or type (e.g., API, USP)',
    isSortable: true,
    isFilterable: true,
  },

  // ========== Therapeutic Area fields ==========
  {
    key: 'therapeutic_areas',
    label: 'Therapeutic Area',
    group: 'Therapeutic Area fields',
    surfaces: ['search', 'modal'],
    dataType: 'array',
    primitiveType: 'object',
    isRelation: true,
    isSortable: true,
    isFilterable: true,
    isSearchable: true,
    relationConfig: {
      type: 'many-to-many',
      entityName: 'therapeutic_areas',
      idField: 'therapeutic_area_id',
      displayField: 'therapeutic_area_name',
      searchField: 'therapeutic_area_name',
      dataSource: 'api',
      apiEndpoint: '/therapeutic_areas',
    },
    renderConfig: {
      width: 200,
      maxDisplayItems: 2,
    },
  },
  {
    key: 'therapeutic_area_name',
    // "Therapeutic Area Name" (not just "Therapeutic Area") — same
    // label-collision fix as facility_name above, vs. the pre-existing
    // therapeutic_area_id ID-based dropdown (label 'Therapeutic Area').
    label: 'Therapeutic Area Name',
    group: 'Therapeutic Area fields',
    // Promoted to a first-class scalar search field (issue #585 Task 6)
    surfaces: ['grid', 'search', 'export'],
    dataType: 'string',
    description: 'Therapeutic area classification',
    isSortable: true,
    isFilterable: true,
    isSearchable: true,
  },

  // ========== Orange Book fields ==========
  {
    key: 'ob_product_name',
    label: 'Orange Book Product Name',
    group: 'Orange Book fields',
    surfaces: ['grid', 'search', 'modal', 'export'],
    dataType: 'string',
    isReadOnly: true,
    isSortable: true,
    isFilterable: true,
    isSearchable: true,
    description: 'Ingredient name from the FDA Orange Book (most recent entry)',
    renderConfig: {
      width: 250,
      maxDisplayLength: 100,
    },
  },
  {
    key: 'orange_book_ingredient',
    label: 'Orange Book Ingredient (All Links)',
    group: 'Orange Book fields',
    // Promoted to a grid column (issue #585 Task 8) — see 'manufacturer'
    // above for why isFilterable stays false in the grid.
    surfaces: ['grid', 'search'],
    dataType: 'array',
    primitiveType: 'string',
    isSortable: false,
    isFilterable: false,
    isSearchable: true,
    description:
      'Orange Book ingredient name across every linked OB record for this product (a product can link to several)',
    renderConfig: { width: 250, maxDisplayItems: 3 },
  },
  {
    key: 'is_fda_linked',
    label: 'Orange Book Linked',
    group: 'Orange Book fields',
    surfaces: ['grid', 'search', 'modal', 'export'],
    dataType: 'boolean',
    isReadOnly: true,
    isSortable: true,
    isFilterable: true,
    isSearchable: true,
    description: 'Indicates whether the product is linked to Orange Book data',
    selectionOptions: [
      { value: 'true', label: 'Yes' },
      { value: 'false', label: 'No' },
    ],
    renderConfig: { width: 120 },
  },

  // ========== NDC Fields ==========
  {
    key: 'ndc_active_ingredient',
    label: 'NDC Active Ingredient',
    group: 'NDC Fields',
    // Promoted to a grid column (issue #585 Task 8) — see 'manufacturer'
    // above for why isFilterable stays false in the grid.
    surfaces: ['grid', 'search'],
    dataType: 'array',
    primitiveType: 'string',
    isSortable: false,
    isFilterable: false,
    isSearchable: true,
    description:
      'NDC active_ingredients across every linked NDC record for this product',
    renderConfig: { width: 250, maxDisplayItems: 3 },
  },
  {
    key: 'is_ndc_linked',
    label: 'NDC Linked',
    group: 'NDC Fields',
    surfaces: ['grid', 'search', 'modal', 'export'],
    dataType: 'boolean',
    isReadOnly: true,
    isSortable: true,
    isFilterable: true,
    isSearchable: true,
    description:
      'Indicates whether the product is linked to FDA NDC Directory data',
    selectionOptions: [
      { value: 'true', label: 'Yes' },
      { value: 'false', label: 'No' },
    ],
    renderConfig: { width: 120 },
  },

  // ========== USDMF Fields ==========
  {
    key: 'is_dmf_linked',
    label: 'USDMF Linked',
    group: 'USDMF Fields',
    surfaces: ['grid', 'search', 'modal', 'export'],
    dataType: 'boolean',
    isReadOnly: true,
    isSortable: true,
    isFilterable: true,
    isSearchable: true,
    description:
      'Indicates whether the product is linked to a USDMF (Drug Master File)',
    selectionOptions: [
      { value: 'true', label: 'Yes' },
      { value: 'false', label: 'No' },
    ],
    renderConfig: { width: 120 },
  },
  {
    key: 'dmf_number',
    label: 'DMF Number',
    group: 'USDMF Fields',
    // Multi-value (issue #585 Task 10.1) — a product can link to more than
    // one DMF record; index-paired with dmf_type at the same array index
    // (attachMultiValueFields' single-query, single-ORDER-BY guarantee).
    // isFilterable false in the grid — see 'manufacturer' (Task 8) for why.
    surfaces: ['grid', 'search', 'export'],
    dataType: 'array',
    primitiveType: 'string',
    isSortable: false,
    isFilterable: false,
    isSearchable: true,
    description: 'DMF number for every linked USDMF record (Type II/IV only)',
    renderConfig: { width: 200, maxDisplayItems: 3 },
  },
  {
    key: 'dmf_type',
    label: 'DMF Type',
    group: 'USDMF Fields',
    // Index-paired with dmf_number — see there. Rendered per-element via
    // DMF_TYPE_LABELS (shared with DMFPanel.tsx) as "Type II (API)"/
    // "Type IV (Excipient)", not the raw 'II'/'IV' code.
    surfaces: ['grid', 'search', 'export'],
    dataType: 'array',
    primitiveType: 'string',
    isSortable: false,
    isFilterable: false,
    isSearchable: true,
    description: 'DMF type for every linked USDMF record (Type II/IV only)',
    renderConfig: { width: 200, maxDisplayItems: 3 },
    // Advanced Search renders this as a selection control, not free text —
    // MATCHABLE_DMF_TYPES (api/sql/dmf.js) only ever produces 'II'/'IV', so
    // free-text search would just make users guess the raw code.
    selectionOptions: [
      { value: 'II', label: 'Type II (API)' },
      { value: 'IV', label: 'Type IV (Excipient)' },
    ],
  },

  // ========== Chemical Properties ==========
  {
    key: 'molecular_formula',
    label: 'Molecular Formula',
    group: 'Chemical Properties',
    surfaces: ['grid', 'search', 'modal', 'export'],
    dataType: 'string',
    isSortable: true,
    isFilterable: true,
    isSearchable: true,
    renderConfig: {
      width: 180,
      maxDisplayLength: 100,
      formatAs: 'html',
    },
  },
  {
    key: 'molecular_mass',
    label: 'Molecular Mass',
    group: 'Chemical Properties',
    surfaces: ['grid', 'search', 'modal', 'export'],
    dataType: 'string', // Keep as string per user requirement
    isSortable: true,
    isFilterable: true,
    isSearchable: true,
    renderConfig: { width: 150 },
  },
  {
    key: 'uri',
    label: 'URI',
    group: 'Chemical Properties',
    surfaces: ['grid', 'search', 'export'],
    dataType: 'string',
    isSortable: true,
    isFilterable: true,
    isSearchable: true,
    renderConfig: { width: 200 },
  },
  {
    key: 'inchi',
    label: 'InChI',
    group: 'Chemical Properties',
    surfaces: ['grid', 'search', 'export'],
    dataType: 'string',
    isSortable: true,
    isFilterable: true,
    isSearchable: true,
    renderConfig: {
      width: 250,
      maxDisplayLength: 100,
    },
  },
  {
    key: 'inchi_key',
    label: 'InChI Key',
    group: 'Chemical Properties',
    // Was explicitly excluded from Advanced Search (issue #585 Task 5.3 fix)
    surfaces: ['grid', 'search', 'export'],
    dataType: 'string',
    isSortable: true,
    isFilterable: true,
    isSearchable: true,
    renderConfig: { width: 200 },
  },
  {
    key: 'smile',
    label: 'SMILES',
    group: 'Chemical Properties',
    surfaces: ['grid', 'search', 'export'],
    dataType: 'string',
    isSortable: true,
    isFilterable: true,
    isSearchable: true,
    renderConfig: {
      width: 250,
      maxDisplayLength: 100,
    },
  },
  {
    key: 'canonical_smile',
    label: 'Canonical SMILES',
    group: 'Chemical Properties',
    surfaces: ['grid', 'search', 'export'],
    dataType: 'string',
    isSortable: true,
    isFilterable: true,
    isSearchable: true,
    renderConfig: {
      width: 250,
      maxDisplayLength: 100,
    },
  },

  // ========== Additional Product Data ==========
  {
    key: 'synonyms',
    label: 'Synonyms',
    group: 'Additional Product Data',
    // Promoted to a grid column (issue #585 Task 8). Not modal-registry-
    // driven (read directly by createPreferredProductNameField's
    // synonyms_radio picker). isFilterable false in the grid — see
    // 'manufacturer' above for why (MUI's default array-filter fallback
    // isn't a real per-item match; Advanced Search is unaffected).
    surfaces: ['grid', 'search'],
    dataType: 'array',
    primitiveType: 'string',
    isSortable: false,
    isFilterable: false,
    isSearchable: true,
    renderConfig: {
      width: 300,
      maxDisplayItems: 3,
      // CAS-source synonym strings can contain HTML markup (e.g. <em> for
      // species names) — strip it for grid display rather than showing the
      // raw tags as literal text.
      formatAs: 'html',
    },
  },
  {
    key: 'replaced_rns',
    label: 'Replaced RNs',
    group: 'Additional Product Data',
    // Promoted to a grid column (issue #585 Task 8) — see 'synonyms' above.
    surfaces: ['grid', 'search'],
    dataType: 'array',
    primitiveType: 'string',
    isSortable: false,
    isFilterable: false,
    isSearchable: true,
    renderConfig: {
      width: 250,
      maxDisplayItems: 3,
    },
  },
  {
    key: 'note',
    label: 'Note',
    group: 'Additional Product Data',
    surfaces: ['grid', 'search', 'modal', 'export'],
    dataType: 'string',
    isSortable: true,
    isFilterable: true,
    isSearchable: true,
    renderConfig: {
      width: 250,
      maxDisplayLength: 100,
      rows: 4,
    },
  },
  {
    key: 'comments',
    label: 'Comments',
    group: 'Additional Product Data',
    // Already selected in queryProductsFlat()'s outer SELECT (f.comments —
    // issue #585 Task 10.2) and already has a PRODUCTS_FLAT_FIELD_MAPPING
    // entry, so no backend change was needed for grid/search. Source is
    // actually dbo.Facilities.comments (this row's facility), not a
    // Products column — each flat row is one product×facility pairing, so
    // a single scalar value per row is well-defined here. Not in the modal
    // surface: the hierarchical Product type has no single "the" comments
    // value (facilities is an array, each with its own FacilitySummary.
    // comments already), and facilities are hidden from the tabs-on modal
    // pending issue #597 — surfacing this in the modal needs its own
    // design decision, flagged rather than guessed. Not in Basic Search
    // (confirmed out of scope).
    surfaces: ['grid', 'search', 'export'],
    dataType: 'string',
    isSortable: true,
    isFilterable: true,
    isSearchable: true,
    renderConfig: {
      width: 250,
      maxDisplayLength: 100,
    },
  },

  // ========== Metadata Fields ==========
  {
    key: 'user_id',
    label: 'Created By',
    group: 'Metadata Fields',
    surfaces: ['grid', 'search', 'modal', 'export'],
    dataType: 'string',
    isReadOnly: true,
    isSortable: true,
    isFilterable: true,
    isSearchable: true,
    renderConfig: { width: 200 },
  },
  {
    key: 'created_date',
    label: 'Created Date',
    group: 'Metadata Fields',
    surfaces: ['grid', 'search', 'modal', 'export'],
    dataType: 'date',
    isReadOnly: true,
    isSortable: true,
    isFilterable: true,
    isSearchable: true,
    renderConfig: {
      width: 180,
      formatAs: 'datetime',
    },
  },
  {
    key: 'modified_date',
    label: 'Modified Date',
    group: 'Metadata Fields',
    surfaces: ['grid', 'search', 'modal', 'export'],
    dataType: 'date',
    isReadOnly: true,
    isSortable: true,
    isFilterable: true,
    isSearchable: true,
    renderConfig: {
      width: 180,
      formatAs: 'datetime',
    },
  },
];

// Create field helpers using shared utility
const helpers = createFieldHelpers<Product & FlatProduct>(
  PRODUCT_FIELDS_REGISTRY
);

export const getFieldByKey = helpers.getFieldByKey;
export const getSearchableFields = helpers.getSearchableFields;
export const getFilterableFields = helpers.getFilterableFields;
export const getSortableFields = helpers.getSortableFields;
export const getEditableFields = helpers.getEditableFields;
export const getRelationFields = helpers.getRelationFields;
export const getComputedFields = helpers.getComputedFields;
export const getReadOnlyFields = helpers.getReadOnlyFields;
export const getRequiredFields = helpers.getRequiredFields;

/**
 * Fields belonging to a given surface (grid, search, modal, export), in
 * registry order (which follows the 12-group taxonomy — see `group` on each
 * field). Grid column order, Manage Columns order, and Advanced Search field
 * order all derive from filtering this registry by surface instead of three
 * independently-maintained lists (issue #585 Task 5).
 */
export function getFieldsForSurface(
  surface: 'grid' | 'search' | 'modal' | 'export'
): BaseFieldDefinition<Product & FlatProduct>[] {
  return PRODUCT_FIELDS_REGISTRY.filter(f => f.surfaces?.includes(surface));
}
